import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type GratitudeLayer = 1 | 2 | 3 | 4;

interface GratitudeResponseProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GratitudeResponse({ isOpen, onClose }: GratitudeResponseProps) {
  const { t } = useTranslation();
  const [layer, setLayer] = useState<GratitudeLayer>(1);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [responseType, setResponseType] = useState<'treasure' | 'gift' | 'witness' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [goldenGratitudeOpen, setGoldenGratitudeOpen] = useState(false);

  const reset = useCallback(() => {
    setLayer(1);
    setMessage('');
    setFiles([]);
    setResponseType(null);
    setSubmitted(false);
    setGoldenGratitudeOpen(false);
    onClose();
  }, [onClose]);

  const handleLayer1Yes = useCallback(() => setLayer(2), []);
  const handleLayer2Angel = useCallback(() => setLayer(3), []);
  const handleLayer2Source = useCallback(() => setLayer(3), []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    setFiles((prev) => [...prev, ...Array.from(selected)].slice(0, 3));
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(() => {
    if ((message.trim() || files.length > 0) && responseType) {
      setSubmitted(true);
    }
  }, [message, files, responseType]);

  const handleGoldenGratitudeYes = useCallback(() => {
    setLayer(4);
    setGoldenGratitudeOpen(true);
  }, []);

  const handleGoldenGratitudeLater = useCallback(() => {
    setTimeout(() => reset(), 1800);
  }, [reset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-end p-4 md:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/20 backdrop-blur-[2px]"
        onClick={reset}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative bg-background-50 border border-background-200/80 rounded-xl shadow-lg max-w-sm w-full overflow-hidden max-h-[85vh] overflow-y-auto">
        {/* Close */}
        <button
          onClick={reset}
          className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-foreground-50 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line" />
        </button>

        {/* Layer 1 — The Invitation */}
        {layer === 1 && (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-heart-line text-rose-300 text-lg" />
              <span className="text-sm font-medium text-foreground-800">
                {t('gratitude.title')}
              </span>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              {t('gratitude.layer1.question')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleLayer1Yes}
                className="flex-1 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('gratitude.layer1.yes')}
              </button>
              <button
                onClick={reset}
                className="flex-1 px-4 py-2 rounded-md border border-foreground-200 text-foreground-600 text-sm hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('gratitude.layer1.later')}
              </button>
            </div>
          </div>
        )}

        {/* Layer 2 — The Direction */}
        {layer === 2 && (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-heart-line text-rose-300 text-lg" />
              <span className="text-sm font-medium text-foreground-800">
                {t('gratitude.layer2.title')}
              </span>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5">
              {t('gratitude.layer2.direction')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleLayer2Angel}
                className="w-full px-4 py-2.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer text-left flex items-center gap-2"
              >
                <i className="ri-heart-3-line text-rose-300" />
                {t('gratitude.layer2.toAngel')}
              </button>
              <button
                onClick={handleLayer2Source}
                className="w-full px-4 py-2.5 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer text-left flex items-center gap-2"
              >
                <i className="ri-sparkling-line text-amber-400" />
                {t('gratitude.layer2.toSource')}
              </button>
            </div>
            <button
              onClick={() => setLayer(1)}
              className="mt-3 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
            >
              {t('gratitude.back')}
            </button>
          </div>
        )}

        {/* Layer 3 — The Living Flow */}
        {layer === 3 && !submitted && (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-heart-line text-rose-300 text-lg" />
              <span className="text-sm font-medium text-foreground-800">
                {t('gratitude.layer3.title')}
              </span>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              {t('gratitude.layer3.prompt')}
            </p>

            {/* Textarea */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={300}
              rows={4}
              className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
              placeholder={t('gratitude.layer3.placeholder')}
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">
              {message.length} / 300 {t('gratitude.layer3.wordLimit')}
            </p>

            {/* Response Type — Treasure / Gift / Witness */}
            <div className="mt-3">
              <p className="text-xs text-foreground-400 mb-2">
                {t('gratitude.layer3.responseType')}
              </p>
              <div className="flex gap-2">
                {(['treasure', 'gift', 'witness'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setResponseType(responseType === type ? null : type)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                      responseType === type
                        ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-foreground-200 text-foreground-500 hover:border-foreground-300'
                    }`}
                  >
                    <i
                      className={`${
                        type === 'treasure'
                          ? 'ri-seedling-line'
                          : type === 'gift'
                          ? 'ri-gift-line'
                          : 'ri-eye-line'
                      } text-sm`}
                    />
                    {t(`gratitude.layer3.${type}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* File upload */}
            <div className="mt-3">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-foreground-200 text-xs text-foreground-500 hover:bg-foreground-50 transition-colors cursor-pointer">
                <i className="ri-image-add-line" />
                <span>附上圖片 / 影片</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>

              {/* File previews */}
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-md"
                    >
                      <i className={file.type.startsWith('video') ? 'ri-movie-line' : 'ri-image-line'} />
                      <span className="max-w-[100px] truncate">{file.name}</span>
                      <button
                        onClick={() => removeFile(idx)}
                        className="text-foreground-400 hover:text-foreground-600 cursor-pointer"
                        aria-label="Remove file"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setLayer(2)}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
              >
                {t('gratitude.back')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={(!message.trim() && files.length === 0) || !responseType}
                className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('gratitude.layer3.send')}
              </button>
            </div>
          </div>
        )}

        {/* Layer 3.5 — Submitted + Golden Gratitude invitation */}
        {submitted && !goldenGratitudeOpen && (
          <div className="p-5 text-center">
            <i className="ri-heart-3-fill text-rose-300 text-2xl mb-3 inline-block" />
            <p className="text-sm text-foreground-700 font-medium">
              {t('gratitude.submitted')}
            </p>
            <p className="text-xs text-foreground-400 mt-1">
              {t('gratitude.submittedDetail')}
            </p>

            {/* Golden Gratitude invitation */}
            <div className="mt-5 pt-4 border-t border-foreground-100">
              <div className="flex items-center justify-center gap-2 mb-2">
                <i className="ri-heart-3-fill text-amber-400 text-lg" />
                <i className="ri-copper-coin-line text-amber-500 text-base" />
              </div>
              <p className="text-xs text-foreground-600 mb-1">
                {t('gratitude.goldenGratitude.question')}
              </p>
              <p className="text-[10px] text-foreground-400 mb-1">
                {t('gratitude.goldenGratitude.questionEn')}
              </p>
              <p className="text-[10px] text-foreground-400 mb-4">
                {t('gratitude.goldenGratitude.description')}
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={handleGoldenGratitudeYes}
                  className="px-4 py-2 rounded-md bg-amber-400 text-white text-sm hover:bg-amber-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-heart-3-fill" />
                  {t('gratitude.goldenGratitude.yes')}
                </button>
                <button
                  onClick={handleGoldenGratitudeLater}
                  className="px-4 py-2 rounded-md border border-foreground-200 text-foreground-500 text-sm hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('gratitude.goldenGratitude.later')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Layer 4 — Golden Gratitude */}
        {layer === 4 && (
          <div className="p-5 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-heart-3-fill text-amber-400 text-2xl" />
              <i className="ri-copper-coin-line text-amber-500 text-xl" />
            </div>
            <p className="text-sm font-medium text-foreground-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
              Golden Gratitude
            </p>
            <p className="text-xs text-foreground-500 mb-1">感恩回應</p>
            <p className="text-xs text-foreground-400 leading-relaxed mb-5">
              這不是捐款。這是感恩的流動——回應旅程的源頭。你給出的，會回到整個生命之樹系統，陪伴更多生命走回自己的中軸。
            </p>

            {/* Simple appreciation flow — not a payment form */}
            <div className="bg-background-100 border border-foreground-200/60 rounded-lg p-4 mb-4">
              <p className="text-xs text-foreground-500">
                當 Portal 的支付系統準備好時，這裡會是一個安靜、溫柔的感恩空間——沒有催促、沒有目標金額、沒有倒數。
              </p>
            </div>

            <button
              onClick={reset}
              className="px-4 py-2 rounded-md bg-foreground-100 text-foreground-600 text-sm hover:bg-foreground-200 transition-colors cursor-pointer"
            >
              關閉
            </button>
          </div>
        )}
      </div>
    </div>
  );
}